import { useEffect, useMemo, useState } from "react";

import { InterpolantSupport3D, type SupportMapRotation } from "./InterpolantSupport3D";
import {
  DESIGN_DIMENSIONS,
  INTERPOLANT_DOMAIN,
  type ArtifactNode,
  type EvaluationResult,
} from "./interpolantProvenanceModel";
import "./InterpolantSlideAnimation.css";

export type EvidenceTone = "interpolated" | "boundary" | "extrapolated";

export type InterpolantAnimationStep = {
  artifactId: string;
  emphasis: "inside" | "boundary" | "extrapolated";
  envelopeLabel: string;
  evidenceId: string;
  evidenceTone: EvidenceTone;
  flightState: string;
  flightStateDetail: string;
  id: string;
  presetId: string;
  rotation: SupportMapRotation;
  supportCopy: string;
  talkTrack: string[];
  title: string;
};

export const INTERPOLANT_ANIMATION_STEPS: InterpolantAnimationStep[] = [
  {
    id: "control-margin-in-envelope",
    artifactId: "pitch-margin",
    emphasis: "inside",
    envelopeLabel: "in envelope",
    evidenceId: "aDB-CERT-042.CM_MARGIN",
    evidenceTone: "interpolated",
    flightState: "APP-042 approach, nominal power-on trim",
    flightStateDetail: "alpha 4.8 deg · Mach 0.18 · flaps 6 deg · balanced propulsors",
    presetId: "cruise",
    rotation: { x: -0.2, y: 0.28, z: 0 },
    supportCopy: "Inside every recorded 12D bound and close to local CFD support.",
    title: "Control margin · in-envelope",
    talkTrack: [
      "First row: one aDB scalar for control margin at a real certification flight state.",
      "Thread replays the 12D flight condition, projects it into the Tailor reduced basis, and sees local CFD support around the query point.",
    ],
  },
  {
    id: "control-margin-boundary",
    artifactId: "pitch-margin",
    emphasis: "boundary",
    envelopeLabel: "boundary",
    evidenceId: "aDB-CERT-057.CM_MARGIN",
    evidenceTone: "boundary",
    flightState: "APP-057 high-alpha trim boundary",
    flightStateDetail: "alpha 14.0 deg · Mach 0.20 · flaps 12 deg · aft CG trim",
    presetId: "control-boundary",
    rotation: { x: -0.42, y: 1.72, z: -0.04 },
    supportCopy: "Still inside the sampled domain, but alpha is exactly on the recorded max.",
    title: "Control margin · envelope boundary",
    talkTrack: [
      "Second row: the same derived quantity at a different flight state.",
      "The query has no slack left on alpha. It is not extrapolated yet, but the evidence is visibly boundary evidence.",
    ],
  },
  {
    id: "control-margin-extrapolated",
    artifactId: "pitch-margin",
    emphasis: "extrapolated",
    envelopeLabel: "extrapolated",
    evidenceId: "aDB-CERT-089.CM_MARGIN",
    evidenceTone: "extrapolated",
    flightState: "APP-089 alpha excursion replay",
    flightStateDetail: "alpha 17.2 deg · Mach 0.18 · flaps 13 deg · trim outside fit",
    presetId: "alpha-outside",
    rotation: { x: -0.62, y: 3.08, z: 0 },
    supportCopy: "The original interpolant is being asked above the sampled alpha envelope.",
    title: "Control margin · extrapolated",
    talkTrack: [
      "Third row: again a single control-margin data point, but the replayed condition is outside the 12D fit.",
      "Because the parent CFD surrogate is extrapolated, the derived control margin inherits that verdict immediately.",
    ],
  },
];

export type ArtifactBeatState = "active" | "revealed" | "queued";

export function artifactBeatState(artifactId: string, step: InterpolantAnimationStep): ArtifactBeatState {
  if (artifactId === step.artifactId) return "active";
  return "queued";
}

function statusClass(tone: EvidenceTone) {
  return `ipd-status ${tone}`;
}

function formatNumber(value: number, digits = 2) {
  return value.toFixed(digits).replace(/\.?0+$/, "");
}

function formatSigned(value: number, digits = 1) {
  const formatted = formatNumber(Math.abs(value), digits);
  return `${value >= 0 ? "+" : "-"}${formatted}`;
}

function formatMargin(value: number) {
  return `${value >= 0 ? "+" : ""}${Math.round(value * 100)}%`;
}

function formatDimensionValue(key: string, value: number) {
  const dimension = DESIGN_DIMENSIONS.find((item) => item.key === key);
  const unit = dimension?.unit ?? "";
  const digits = Math.abs(value) >= 100 ? 0 : unit === "" ? 2 : 1;
  const formatted = value.toFixed(digits).replace(/\.?0+$/, "");
  return `${formatted}${unit ? ` ${unit}` : ""}`;
}

function ControlMarginEvidence({
  evaluation,
  selectedArtifact,
  step,
}: {
  evaluation: EvaluationResult;
  selectedArtifact: ArtifactNode;
  step: InterpolantAnimationStep;
}) {
  const alphaStatus = evaluation.dimensionStatuses.find((dimension) => dimension.key === "alphaDeg");
  const supportCaseSummary = evaluation.supportCases
    .slice(0, 3)
    .map((support) => `${support.id} d ${support.distance.toFixed(2)}`)
    .join(" · ");
  const reasonCopy = evaluation.reasons.join("; ");
  const alphaCopy = alphaStatus
    ? step.evidenceTone === "boundary"
      ? `${alphaStatus.label} ${formatDimensionValue(alphaStatus.key, alphaStatus.value)} = sampled max ${formatDimensionValue(alphaStatus.key, alphaStatus.max)} · provenance margin ${formatMargin(evaluation.provenanceMargin)}`
      : `${alphaStatus.label} ${formatDimensionValue(alphaStatus.key, alphaStatus.value)} · ${reasonCopy}`
    : reasonCopy;

  return (
    <div className="ipd-slide-evidence-stack" aria-label="Control margin audit evidence">
      <div className={`ipd-slide-evidence-card ${step.evidenceTone} is-primary`}>
        <span>Evidence 01 · aDB row</span>
        <strong>{step.evidenceId}</strong>
        <small>{selectedArtifact.quantityLabel}: {selectedArtifact.value}</small>
      </div>
      <div className="ipd-slide-evidence-card">
        <span>Evidence 02 · flight state</span>
        <strong>{step.flightState}</strong>
        <small>{step.flightStateDetail}</small>
      </div>
      <div className={`ipd-slide-evidence-card ${step.evidenceTone}`}>
        <span>Evidence 03 · envelope replay</span>
        <strong>{step.envelopeLabel}</strong>
        <small>{alphaCopy}</small>
      </div>
      <div className="ipd-slide-evidence-card">
        <span>Evidence 04 · local support</span>
        <strong>d {evaluation.nearestSupportDistance.toFixed(2)} / r {INTERPOLANT_DOMAIN.trustRadius.toFixed(2)}</strong>
        <small>{supportCaseSummary || step.supportCopy}</small>
      </div>
    </div>
  );
}

function ProvenanceMeter({ evaluation }: { evaluation: EvaluationResult }) {
  const width = Math.max(0, Math.min(100, 50 + evaluation.provenanceMargin * 100));

  return (
    <div className="ipd-slide-meter" aria-label="Current provenance margin">
      <div>
        <span>Provenance margin</span>
        <strong className={evaluation.mode}>{formatMargin(evaluation.provenanceMargin)}</strong>
      </div>
      <div className="ipd-slide-meter-track">
        <span className="ipd-slide-meter-zero" />
        <span className={evaluation.mode} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

export function InterpolantSlideAnimation({
  activeStepIndex,
  evaluation,
  onSelectStep,
  selectedArtifact,
}: {
  activeStepIndex: number;
  evaluation: EvaluationResult;
  onSelectStep: (index: number) => void;
  selectedArtifact: ArtifactNode;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const step = INTERPOLANT_ANIMATION_STEPS[activeStepIndex] ?? INTERPOLANT_ANIMATION_STEPS[0]!;
  const latent = evaluation.latent.map((axis) => axis.value) as [number, number, number];
  const nextStepIndex = (activeStepIndex + 1) % INTERPOLANT_ANIMATION_STEPS.length;
  const previousStepIndex = (activeStepIndex + INTERPOLANT_ANIMATION_STEPS.length - 1) % INTERPOLANT_ANIMATION_STEPS.length;
  const basisCopy = useMemo(
    () =>
      evaluation.latent.map((axis) => ({
        label: axis.label.replace(" mode", ""),
        value: axis.value,
      })),
    [evaluation.latent],
  );

  useEffect(() => {
    if (!isPlaying) return undefined;
    const timer = window.setTimeout(() => onSelectStep(nextStepIndex), 3800);
    return () => window.clearTimeout(timer);
  }, [isPlaying, nextStepIndex, onSelectStep]);

  useEffect(() => {
    setIsTransitioning(true);
    const timer = window.setTimeout(() => setIsTransitioning(false), 760);
    return () => window.clearTimeout(timer);
  }, [activeStepIndex]);

  return (
    <section
      className={`ipd-slide-animation ${step.emphasis}`}
      data-transitioning={isTransitioning}
      data-testid="interpolant-slide-animation"
      data-step={activeStepIndex + 1}
      aria-label="Interpolant provenance screen"
    >
      <div className="ipd-slide-frame">
        <header className="ipd-slide-header">
          <div>
            <h2>{step.title}</h2>
          </div>
          <div className="ipd-slide-status">
            <span className={statusClass(step.evidenceTone)} data-testid="slide-animation-status">{step.envelopeLabel}</span>
            <strong>{step.evidenceId}</strong>
            <small>{selectedArtifact.quantityLabel}: {selectedArtifact.value}</small>
          </div>
        </header>

        <div className="ipd-slide-grid">
          <article className="ipd-slide-talk" data-testid="slide-talking-points">
            <div className="ipd-slide-proof">Verdict = 12D bounds + reduced-basis support + lineage inheritance</div>
            <ul>
              {step.talkTrack.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <div className="ipd-slide-figure" data-testid="slide-3d-figure">
            <InterpolantSupport3D
              latent={latent}
              mode={evaluation.mode}
              pointTone={step.evidenceTone}
              rotation={step.rotation}
              supportCases={evaluation.supportCases}
            />
          </div>

          <aside className="ipd-slide-audit" aria-label="Animated audit evidence">
            <ControlMarginEvidence evaluation={evaluation} selectedArtifact={selectedArtifact} step={step} />

            <div className="ipd-slide-basis" aria-label="Current reduced aircraft coordinates">
              {basisCopy.map((axis) => (
                <div key={axis.label}>
                  <span>{axis.label}</span>
                  <strong>{formatSigned(axis.value, 2)}</strong>
                </div>
              ))}
            </div>

            <ProvenanceMeter evaluation={evaluation} />
          </aside>
        </div>

        <div className="ipd-slide-stepper" role="group" aria-label="Slide animation steps">
          <div className="ipd-slide-progress" aria-hidden="true">
            <span style={{ width: `${((activeStepIndex + 1) / INTERPOLANT_ANIMATION_STEPS.length) * 100}%` }} />
          </div>
          <button type="button" data-testid="slide-animation-prev" onClick={() => onSelectStep(previousStepIndex)}>
            Prev
          </button>
          {INTERPOLANT_ANIMATION_STEPS.map((item, index) => (
            <button
              type="button"
              aria-pressed={index === activeStepIndex}
              data-testid={`slide-animation-step-${index + 1}`}
              key={item.id}
              onClick={() => onSelectStep(index)}
            >
              {index + 1}
            </button>
          ))}
          <button type="button" data-testid="slide-animation-next" onClick={() => onSelectStep(nextStepIndex)}>
            Next
          </button>
          <button
            type="button"
            aria-pressed={isPlaying}
            data-testid="slide-animation-play"
            onClick={() => setIsPlaying((current) => !current)}
          >
            {isPlaying ? "Pause" : "Play"}
          </button>
        </div>
      </div>
    </section>
  );
}
